import { parsePhoneNumberFromString } from "libphonenumber-js";
import { DEFAULT_COUNTRY_CODE } from "src/components/constant/constant";

/** Digits only — for national number input (country code is selected separately). */
export const sanitizePhone = (phone: string): string => phone.replace(/\D/g, "");

/** Ensure calling code has a leading '+'. */
export function normalizeCountryCode(countryCode: string): string {
  const trimmed = countryCode.trim();

  if (!trimmed) return DEFAULT_COUNTRY_CODE;
  return trimmed.startsWith("+") ? trimmed : `+${trimmed}`;
}

/** Join country code + national digits for parsing. */
export function joinPhone(countryCode: string, national: string): string {
  return `${normalizeCountryCode(countryCode)}${sanitizePhone(national)}`;
}

/** Canonical E.164 for API payloads (e.g. +61364493004). Null when not a valid number. */
export function toE164(countryCode: string, national: string): string | null {
  const parsed = parsePhoneNumberFromString(joinPhone(countryCode, national));

  return parsed?.isValid() ? parsed.number : null;
}

/** Normalize a full phone string (already includes country code). Idempotent for valid E.164. */
export function normalizeE164FromString(phone: string | null | undefined): string | null {
  const trimmed = phone?.trim();

  if (!trimmed) return null;
  const parsed = parsePhoneNumberFromString(trimmed);

  return parsed?.isValid() ? parsed.number : null;
}

export type ValidatePhoneFieldsOptions = {
  required?: boolean;
  requiredMessage?: string;
  invalidMessage?: string;
};

/** Validate country + national fields; returns an error message or null when valid. */
export function validatePhoneFields(
  countryCode: string,
  national: string,
  options: ValidatePhoneFieldsOptions = {}
): string | null {
  const {
    required = true,
    requiredMessage = "Phone is required",
    invalidMessage = "Invalid contact number"
  } = options;

  if (!sanitizePhone(national)) {
    return required ? requiredMessage : null;
  }
  if (!toE164(countryCode, national)) {
    return invalidMessage;
  }
  return null;
}

/**
 * On blur: if the value is a valid number, return canonical split fields for the form.
 * Otherwise return sanitized national digits with the current country code.
 */
export function coercePhoneFields(
  countryCode: string,
  national: string,
  fallbackCountryCode: string = DEFAULT_COUNTRY_CODE
): { phoneCountryCode: string; phone: string } {
  const code = normalizeCountryCode(countryCode || fallbackCountryCode);
  const e164 = toE164(code, national) ?? normalizeE164FromString(national);

  if (e164) return splitPhoneFields(e164, fallbackCountryCode);
  return { phoneCountryCode: code, phone: sanitizePhone(national) };
}

/** Split stored/API E.164 into form fields (national number without trunk 0). */
export function splitPhoneFields(
  e164: string | null | undefined,
  fallbackCountryCode: string = DEFAULT_COUNTRY_CODE
): { phoneCountryCode: string; phone: string } {
  const normalized = normalizeE164FromString(e164);

  if (!normalized) {
    return { phoneCountryCode: normalizeCountryCode(fallbackCountryCode), phone: "" };
  }
  const parsed = parsePhoneNumberFromString(normalized);

  if (!parsed?.isValid()) {
    return { phoneCountryCode: normalizeCountryCode(fallbackCountryCode), phone: "" };
  }
  return {
    phoneCountryCode: `+${parsed.countryCallingCode}`,
    phone: parsed.nationalNumber
  };
}

/** Human-readable format for review UI and tables. Falls back to raw input when invalid. */
export function formatPhoneDisplay(phone: string | null | undefined): string {
  const normalized = normalizeE164FromString(phone);

  if (!normalized) return phone?.trim() ?? "";
  const parsed = parsePhoneNumberFromString(normalized);

  return parsed?.isValid() ? parsed.formatInternational() : normalized;
}

/**
 * Last-line normalization before GraphQL mutations.
 * Returns canonical E.164 or empty string — never passes through invalid values.
 */
export function normalizePhoneForApi(phone: string | null | undefined): string {
  if (!phone?.trim()) return "";
  return normalizeE164FromString(phone) ?? "";
}

/** Build API phone from form fields; returns empty string when invalid. */
export function phoneFieldsToApi(countryCode: string, national: string): string {
  return toE164(countryCode, national) ?? "";
}
